import { FunctionComponent, useState } from 'react'
import { DaycareGroup, DaycareGroupType } from '@/types/DaycareGroup'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import UserInput from '@/components/_custom/UserInput/userInput'
import styles from './editGroupInformationDialog.module.scss'
import { SelectOptionsDeprecated } from '@/types/Select'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useMutation } from '@apollo/client'
import { UPDATE_DAYCARE_GROUP, UPDATE_DAYCARE_GROUP_ACTIVE } from '@/modules/API/daycareGroup/daycareGroupHandler'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface EditGroupInformationDialogProps {
    open: boolean
    handleClose: () => void
    group: DaycareGroup
    refetchGroups: () => void
}

const EditGroupInformationDialog: FunctionComponent<EditGroupInformationDialogProps> = ({
    open,
    handleClose,
    group,
    refetchGroups
}) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [groupNameEdit, setGroupNameEdit] = useState<string>(group.name)
    const [countryEdit, setCountryEdit] = useState<string>(group.country)
    const [locationEdit, setLocationEdit] = useState<string>(group.location)
    const [typeEdit, setTypeEdit] = useState<string>(group.type)
    const [activeEdit, setActiveEdit] = useState<boolean>(group.active || false)

    const [updateGroup] = useMutation(UPDATE_DAYCARE_GROUP, {
        variables: {
            groupId: group.id,
            group: {
                name: groupNameEdit,
                country: countryEdit,
                location: locationEdit,
                type: typeEdit
            }
        },
        onCompleted: () => {
            showNotificationSuccess(t('groupUpdated'))

            refetchGroups()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedGroup'))
        }
    })

    const [updateActiveGroup] = useMutation(UPDATE_DAYCARE_GROUP_ACTIVE, {
        variables: {
            groupId: group.id,
            active: activeEdit
        },
        onCompleted: () => {
            showNotificationSuccess(t('groupUpdated'))

            refetchGroups()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedGroup'))
        }
    })

    function handleUpdate(): void {
        if (activeEdit !== group.active) {
            updateActiveGroup()
        }

        if (
            groupNameEdit !== group.name ||
            countryEdit !== group.country ||
            locationEdit !== group.location ||
            typeEdit !== group.type
        ) {
            updateGroup()
        }
    }

    function setGroupTypeConverter(value: string): void {
        switch (value) {
            case DaycareGroupType.agglomeration:
                setTypeEdit(DaycareGroupType.agglomeration)
                break
            case DaycareGroupType.association:
                setTypeEdit(DaycareGroupType.association)
                break
            case DaycareGroupType.city:
                setTypeEdit(DaycareGroupType.city)
                break
            case DaycareGroupType.private:
                setTypeEdit(DaycareGroupType.private)
                break
            case DaycareGroupType.community:
                setTypeEdit(DaycareGroupType.community)
                break
            case DaycareGroupType.county:
                setTypeEdit(DaycareGroupType.county)
                break
        }
    }

    const selectDaycareType: SelectOptionsDeprecated = [
        {
            options: [
                { value: DaycareGroupType.agglomeration, name: t('agglomeration') },
                { value: DaycareGroupType.association, name: t('association') },
                { value: DaycareGroupType.community, name: t('community') },
                { value: DaycareGroupType.private, name: t('private') },
                { value: DaycareGroupType.county, name: t('county') },
                { value: DaycareGroupType.city, name: t('city') }
            ]
        }
    ]

    const disabled: boolean =
        groupNameEdit === group.name &&
        countryEdit === group.country &&
        locationEdit === group.location &&
        typeEdit === group.type &&
        activeEdit === group.active

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('editGroup')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('name')}
                        className={styles.UserInput}
                        value={groupNameEdit}
                        setValue={setGroupNameEdit}
                        format={'pascal'}
                    />
                    <div className={styles.UserInput}>
                        <CountrySelect country={countryEdit} setCountry={setCountryEdit} required />
                    </div>
                </div>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('location')}
                        className={styles.UserInput}
                        value={locationEdit}
                        setValue={setLocationEdit}
                        format={'upper'}
                        unallowedCharacters={'num'}
                    />
                    <SelectUnique
                        label={t('type')}
                        setSelectValue={setGroupTypeConverter}
                        selectValue={typeEdit}
                        selectOptions={selectDaycareType}
                        formClassName={styles.UserInput}
                    />
                </div>
                <div className={styles.CheckBoxContainer}>
                    <Checkbox
                        text={t('activeGroup')}
                        checked={activeEdit}
                        onChange={setActiveEdit}
                        className={styles.CheckBox}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'update'}
                        onClick={handleUpdate}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditGroupInformationDialog
